import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { RelationEntry } from '@/features/task-relations';
import type { LabelView } from '@/lib/api/generated/model';
import { guardedLocalStorage } from '@/shared/store/guardedLocalStorage';
import {
  TASK_DRAFTS_STORAGE_KEY,
  registerPersonalDraftStore,
} from '@/shared/store/personalDrafts';
import {
  createUploadActions,
  isPending,
  type StagedAttachment,
  type UploadQueue,
} from './draftUploads';

export { TASK_DRAFTS_STORAGE_KEY };

/** At most one draft per board (spec 121 FR-004). */
export function draftKey(workspaceId: string, workflowId: string) {
  return `${workspaceId}:${workflowId}`;
}

/** The queue a task's drawer uploads through: not a draft, the same machinery. */
export function taskQueueId(taskId: string) {
  return `task:${taskId}`;
}

/** What someone has entered for a task not yet created. */
export interface TaskDraft {
  workspaceId: string;
  workflowId: string;
  title: string;
  description: string;
  labelIds: string[];
  /** Labels made from the dialog's picker, so a restored draft can show them. */
  createdLabels: LabelView[];
  relations: RelationEntry[];
  /** Files staged before a reload: named, since a page cannot keep a `File`. */
  lostFileNames: string[];
  updatedAt: string;
  /**
   * Set once Create succeeds. The task exists, so the draft is no longer
   * written to storage — a reload must never offer to create it twice — and it
   * lingers only while its uploads finish.
   */
  createdTaskId: string | null;
}

export type DraftFields = Pick<
  TaskDraft,
  'title' | 'description' | 'labelIds' | 'createdLabels' | 'relations'
>;

interface PersistedDraft extends DraftFields {
  workspaceId: string;
  workflowId: string;
  fileNames: string[];
  updatedAt: string;
}

/** Everything `discardDraft` took away, files included, for Undo. */
export interface DiscardedDraft {
  key: string;
  draft: TaskDraft;
  queue: UploadQueue | undefined;
}

/**
 * Whether closing should keep the draft (FR-001): anything typed, chosen or
 * staged. An untouched dialog leaves nothing behind.
 */
export function hasContent(
  draft: TaskDraft,
  staged: readonly StagedAttachment[] = [],
) {
  return (
    draft.title.trim() !== '' ||
    draft.description.trim() !== '' ||
    draft.labelIds.length > 0 ||
    draft.relations.length > 0 ||
    staged.length > 0 ||
    draft.lostFileNames.length > 0
  );
}

export interface TaskDraftState {
  drafts: Record<string, TaskDraft>;
  queues: Record<string, UploadQueue>;
  /** The one draft shown as a dialog; the rest are chips. */
  expandedKey: string | null;
  /** Bumped on each minimize, so the dock's live region can say "Draft saved". */
  minimizedCount: number;

  /** Expands the board's draft, creating it if there is none (FR-004). */
  openDraft: (workspaceId: string, workflowId: string) => string;
  expand: (key: string) => void;
  updateDraft: (key: string, fields: Partial<DraftFields>) => void;
  /** Folds the dialog into a chip — or drops it, when nothing was entered. */
  minimize: (key: string) => void;
  dropDraft: (key: string) => void;
  discardDraft: (key: string) => DiscardedDraft | null;
  restoreDiscarded: (snapshot: DiscardedDraft) => void;
  markCreated: (key: string, taskId: string) => void;
  /** The task exists and nothing is left to send: the draft and its queue go. */
  clearDraft: (key: string) => void;

  stageFiles: (
    queueId: string,
    files: readonly File[],
    options?: { replace?: boolean },
  ) => void;
  removeFile: (queueId: string, id: string) => void;
  retryUpload: (queueId: string, id: string) => void;
  startUploads: ReturnType<typeof createUploadActions>['startUploads'];
}

const emptyDraft = (workspaceId: string, workflowId: string): TaskDraft => ({
  workspaceId,
  workflowId,
  title: '',
  description: '',
  labelIds: [],
  createdLabels: [],
  relations: [],
  lostFileNames: [],
  updatedAt: new Date().toISOString(),
  createdTaskId: null,
});

function without<T>(record: Record<string, T>, key: string) {
  return Object.fromEntries(
    Object.entries(record).filter(([candidate]) => candidate !== key),
  );
}

/**
 * Task drafts (spec 121): what someone began entering in the Create Task
 * dialog, kept when the dialog closes, across pages and across a reload.
 *
 * The text and choices are persisted; staged files and the upload queue are
 * not — a page cannot keep a picked `File` — so a rehydrated draft names the
 * files it lost instead of dropping them silently (FR-009). The queue lives
 * here, not in the dialog, so uploads outlive a minimize or a page change.
 *
 * A draft is the person's own unfinished input, never service data — the same
 * line `preferences.ts` draws (constitution §4).
 */
export const useTaskDraftStore = create<TaskDraftState>()(
  persist(
    (set, get) => {
      const uploads = createUploadActions(get, set);
      const touch = (key: string, patch: Partial<TaskDraft>) => {
        const draft = get().drafts[key];
        if (!draft) return;
        set({
          drafts: {
            ...get().drafts,
            [key]: { ...draft, ...patch, updatedAt: new Date().toISOString() },
          },
        });
      };
      const remove = (key: string) => {
        set({
          drafts: without(get().drafts, key),
          queues: without(get().queues, key),
          expandedKey: get().expandedKey === key ? null : get().expandedKey,
        });
      };

      return {
        drafts: {},
        queues: {},
        expandedKey: null,
        minimizedCount: 0,

        openDraft: (workspaceId, workflowId) => {
          const key = draftKey(workspaceId, workflowId);
          const drafts = get().drafts;
          set({
            drafts: drafts[key]
              ? drafts
              : { ...drafts, [key]: emptyDraft(workspaceId, workflowId) },
            expandedKey: key,
          });
          return key;
        },

        expand: (key) => {
          if (get().drafts[key]) set({ expandedKey: key });
        },

        updateDraft: (key, fields) => {
          touch(key, fields);
        },

        minimize: (key) => {
          const draft = get().drafts[key];
          if (!draft) return;
          const staged = get().queues[key]?.items ?? [];
          // Nothing entered: nothing to keep. A created task with nothing left
          // to send: nothing to wait for — its failed files' Retry belonged to
          // the dialog being closed (spec edge case).
          const done =
            draft.createdTaskId === null
              ? !hasContent(draft, staged)
              : !staged.some(isPending);
          if (done) {
            remove(key);
            return;
          }
          set({
            expandedKey: get().expandedKey === key ? null : get().expandedKey,
            minimizedCount: get().minimizedCount + 1,
          });
        },

        dropDraft: remove,

        discardDraft: (key) => {
          const draft = get().drafts[key];
          if (!draft) return null;
          const snapshot = { key, draft, queue: get().queues[key] };
          remove(key);
          return snapshot;
        },

        restoreDiscarded: ({ key, draft, queue }) => {
          set({
            drafts: { ...get().drafts, [key]: draft },
            queues: queue ? { ...get().queues, [key]: queue } : get().queues,
          });
        },

        markCreated: (key, taskId) => {
          touch(key, { createdTaskId: taskId });
        },

        clearDraft: remove,

        stageFiles: (queueId, files, options) => {
          uploads.addFiles(queueId, files, options);
          const draft = get().drafts[queueId];
          if (draft && draft.lostFileNames.length > 0) {
            // Adding a file back answers its "needs to be added again".
            const names = new Set(files.map((file) => file.name));
            touch(queueId, {
              lostFileNames: draft.lostFileNames.filter((n) => !names.has(n)),
            });
          }
        },
        removeFile: uploads.removeFile,
        retryUpload: uploads.retryUpload,
        startUploads: uploads.startUploads,
      };
    },
    {
      name: TASK_DRAFTS_STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(() => guardedLocalStorage),
      partialize: (state) => ({
        drafts: Object.fromEntries(
          Object.entries(state.drafts)
            .filter(([, draft]) => draft.createdTaskId === null)
            .map(([key, draft]): [string, PersistedDraft] => [
              key,
              {
                workspaceId: draft.workspaceId,
                workflowId: draft.workflowId,
                title: draft.title,
                description: draft.description,
                labelIds: draft.labelIds,
                createdLabels: draft.createdLabels,
                relations: draft.relations,
                fileNames: [
                  ...draft.lostFileNames,
                  ...(state.queues[key]?.items ?? [])
                    .filter((item) => item.status.kind !== 'refused')
                    .map((item) => item.file.name),
                ],
                updatedAt: draft.updatedAt,
              },
            ]),
        ),
      }),
      merge: (persisted, current) => {
        const stored =
          (persisted as { drafts?: Record<string, PersistedDraft> } | null)
            ?.drafts ?? {};
        const drafts: Record<string, TaskDraft> = {};
        // A created draft is not in storage; it stays while its uploads run.
        for (const [key, draft] of Object.entries(current.drafts)) {
          if (draft.createdTaskId !== null) drafts[key] = draft;
        }
        for (const [key, saved] of Object.entries(stored)) {
          if (drafts[key]) continue;
          const here = new Set(
            (current.queues[key]?.items ?? []).map((item) => item.file.name),
          );
          const { fileNames, ...fields } = saved;
          drafts[key] = {
            ...fields,
            lostFileNames: fileNames.filter((name) => !here.has(name)),
            createdTaskId: null,
          };
        }
        return {
          ...current,
          drafts,
          expandedKey:
            current.expandedKey !== null && drafts[current.expandedKey]
              ? current.expandedKey
              : null,
        };
      },
    },
  ),
);

// Another tab wrote: take its drafts, keep this tab's files (FR-011). The
// `storage` event fires only in the other tabs, which is exactly the case.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === TASK_DRAFTS_STORAGE_KEY) {
      void useTaskDraftStore.persist.rehydrate();
    }
  });
}

// Drafts go with the person (spec 197): emptied when someone else signs in here.
registerPersonalDraftStore(() =>
  useTaskDraftStore.setState({
    drafts: {},
    queues: {},
    expandedKey: null,
    minimizedCount: 0,
  }),
);
